import { useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuItem } from '@mui/material';
import { DescriptionOutlined, MoreHorizRounded, OpenInNewRounded, PlayArrowRounded } from '@mui/icons-material';

import NoteActions, { NoteOptions } from './NoteActions';
import NotesTranscriptPreview, { NotesTranscriptDialog } from './NotesTranscriptPreview';
import { getDisplaySpeakerData, getUnidentifiedSpeakerData } from './speakerHelper';
import { fancyTimeFormat, formatPreviewInfoPublishedDate } from './timeHelper';
import { isEmojiOnlyNote } from './noteTextHelper';
import './Notes.css';

const GroupedNotes = ({ group, votes, voteUp, voteDown, deleteNote, showsShowName = false, track, uid }) => {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [showTranscript, setShowTranscript] = useState(false);
    const note = group.notes[0];
    if (!note) return null;

    const { messageText, timeSeconds, messageId, source, trackId } = note;
    const noteMessage = track?.id === trackId
        ? track.transcript?.find(message => message.id === messageId) : null;
    let speaker = note.speaker;
    if (noteMessage) {
        const transcriptSpeaker = getDisplaySpeakerData(noteMessage.speakerId, track.speakers, noteMessage.anonymousDisplay);
        // A saved portrait remains useful when the loaded registry has no matching speaker.
        if (transcriptSpeaker.id !== 'unknown' || transcriptSpeaker.anonymousDisplay || !speaker) {
            speaker = transcriptSpeaker;
        }
    }
    if (!speaker && messageId) speaker = getUnidentifiedSpeakerData();

    const hasTranscript = Boolean(messageId && messageText);
    const canPlay = timeSeconds !== null && timeSeconds !== undefined && Number.isFinite(Number(timeSeconds)) && Number(timeSeconds) >= 0;
    const openEpisode = () => {
        setMenuAnchor(null);
        navigate(`/track/${encodeURIComponent(trackId)}?autoplay=false`);
    };
    const playFromNote = () => {
        setMenuAnchor(null);
        navigate(`/track/${encodeURIComponent(trackId)}?start=${Number(timeSeconds)}&source=note`);
    };
    const openTranscript = () => {
        setMenuAnchor(null);
        setShowTranscript(true);
    };

    return (
        <div className='noteGroup notesDesign'>
            {source && <div className='notesSourceRow'>
                <button type='button' onClick={openEpisode} className='notesSourceButton' aria-label={`Open episode: ${source.title}`}>
                    {source.imageURL && <img src={source.imageURL} className='notesSourceImage podcastArtwork' alt='' />}
                    <span className='notesSourceMetadata'>
                        <span className='notesSourceTitle'>{source.title}</span>
                        {showsShowName && source.name && <span className='notesSourceShow'>{source.name}</span>}
                    </span>
                </button>
                {(canPlay || hasTranscript || showsShowName) && <>
                    <button type='button' className='notesOptionsButton' onClick={event => setMenuAnchor(event.currentTarget)}
                        aria-label='Episode options' aria-haspopup='menu' aria-expanded={Boolean(menuAnchor)}>
                        <MoreHorizRounded aria-hidden='true' />
                    </button>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ className: 'notesPopup' }}>
                        {canPlay && <MenuItem onClick={playFromNote}><PlayArrowRounded /><span>Play from {fancyTimeFormat(timeSeconds)}</span></MenuItem>}
                        {hasTranscript && <MenuItem onClick={openTranscript}><DescriptionOutlined /><span>View transcript</span></MenuItem>}
                        {showsShowName && <MenuItem onClick={openEpisode}><OpenInNewRounded /><span>Open episode</span></MenuItem>}
                    </Menu>
                </>}
            </div>}

            <div className='notesGroupBody'>
                {hasTranscript && <NotesTranscriptPreview messageId={messageId} messageText={messageText} speaker={speaker} />}
                <div className='notesGroupCards'>
                    {group.notes.map(savedNote => {
                        const date = formatPreviewInfoPublishedDate(savedNote.created);
                        const isOwner = Boolean(uid && savedNote.uid === uid);
                        return <article key={savedNote.id} className='notesCard'>
                            <div className={`notesText${isEmojiOnlyNote(savedNote.text) ? ' notesTextEmojiOnly' : ''}`}>{savedNote.text}</div>
                            <div className='notesCardFooter'>
                                {(isOwner || date) && <div className='notesMetadata'>
                                    {isOwner && <span className='notesAuthor'>You</span>}
                                    {date && <span className='notesDate'>{isOwner ? ' · ' : ''}{date === 'just now' ? 'Just now' : date}</span>}
                                </div>}
                                <div className='notesFooterActions'>
                                    <NoteActions note={savedNote} votes={votes} voteUp={voteUp} voteDown={voteDown} />
                                    <NoteOptions note={savedNote} deleteNote={deleteNote} />
                                </div>
                            </div>
                        </article>;
                    })}
                </div>
            </div>
            <NotesTranscriptDialog open={showTranscript} onClose={() => setShowTranscript(false)} messageId={messageId} />
        </div>
    );
};

const mapStateToProps = state => ({ track: state.data.track, uid: state.user.uid });
export default connect(mapStateToProps)(GroupedNotes);
